// Real OrchardIQ FastAPI backend (forecast/api.py). Responses are normalized into view models
// shared with demo mode (see src/types/orchard.js).
import { addDaysIso, shortDate } from "@/lib/format";

const BASE = (process.env.REACT_APP_ORCHARD_API_URL || process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export const MEASURE_PARAM = { chillHours: "chill_hours", chillPortions: "chill_portions", gdd: "gdd" };
export const METRIC_KEYS = ["chillHours", "chillPortions", "gdd"];

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function get(path, signal) {
  let res;
  try {
    res = await fetch(`${BASE}${path}`, { signal });
  } catch (e) {
    if (e.name === "AbortError") throw e;
    throw new ApiError("Can't reach the OrchardIQ API. Check your connection and try again.", 0);
  }
  if (!res.ok) {
    let detail = "";
    try {
      const body = await res.json();
      detail = typeof body.detail === "string" ? body.detail : JSON.stringify(body.detail);
    } catch {
      detail = res.statusText;
    }
    throw new ApiError(`API error ${res.status}${detail ? ` — ${detail}` : ""}`, res.status);
  }
  return res.json();
}

export const fetchHealth = (signal) =>
  get("/api/health", signal).then((r) => ({
    status: r.status,
    issueDate: r.issue_date,
    source: r.source,
    refreshedAt: r.refreshed_at,
    liveUpdates: r.live_updates,
    modelTrainedThrough: r.model_trained_through,
    lastError: r.last_error,
  }));

export const fetchStations = (signal) =>
  get("/api/stations", signal).then((list) =>
    list.map((s) => ({
      id: s.station,
      slug: s.name.toLowerCase(),
      name: s.name,
      lat: s.latitude,
      lng: s.longitude,
      elevationFt: s.elevation_ft,
    })),
  );

const summaryVM = (metric, r) => {
  const totals = (r.past_seasons_same_date || []).map((p) => p.total).filter((v) => v != null);
  return {
    metric,
    units: r.units,
    issueDate: r.issue_date,
    season: r.season ?? null,
    seasonStart: r.season_start ?? null,
    seasonToDate: r.season_to_date,
    daysMissing: r.days_missing ?? null,
    next7: r.next7,
    normalNext7: r.normal_next7,
    projected: r.projected,
    projectedThrough: r.projected_through ?? null,
    pastSeasons: r.past_seasons_same_date || [],
    pastAvg: r.past_avg_same_date,
    pastMin: totals.length ? Math.min(...totals) : null,
    pastMax: totals.length ? Math.max(...totals) : null,
    pastLabel: totals.length ? `Same date, ${r.past_seasons_same_date.length} past seasons` : null,
    pctVsPast:
      r.pct_vs_past !== undefined
        ? r.pct_vs_past
        : r.past_avg_same_date
          ? Math.round(1000 * (r.season_to_date / r.past_avg_same_date - 1)) / 10
          : null,
    pctDerived: r.pct_vs_past === undefined,
  };
};

const stationLocation = (st) => ({
  kind: "station",
  id: st.id,
  name: st.name,
  lat: st.lat,
  lng: st.lng,
  elevationFt: st.elevationFt,
  nearestStation: { id: st.id, name: st.name },
  nearestKm: 0,
  roughEstimate: false,
  weights: [{ id: st.id, name: st.name, weight: 1 }],
});

export async function fetchStationSummary(station, signal) {
  const rows = await Promise.all(
    METRIC_KEYS.map((m) => get(`/api/stations/${station.id}/summary?measure=${MEASURE_PARAM[m]}`, signal)),
  );
  return {
    location: stationLocation(station),
    metrics: Object.fromEntries(METRIC_KEYS.map((m, i) => [m, summaryVM(m, rows[i])])),
    daily: null,
  };
}

export async function fetchPointSummary(lat, lng, stations, signal) {
  const rows = await Promise.all(
    METRIC_KEYS.map((m) => get(`/api/point?lat=${lat}&lon=${lng}&measure=${MEASURE_PARAM[m]}`, signal)),
  );
  const r = rows[0];
  const nameOf = (id) => stations.find((s) => s.id === Number(id))?.name || `#${id}`;
  return {
    location: {
      kind: "estimated",
      id: "custom",
      name: "Custom orchard site",
      lat: r.lat,
      lng: r.lon,
      elevationFt: null,
      nearestStation: { id: r.nearest_station, name: nameOf(r.nearest_station) },
      nearestKm: r.nearest_km,
      roughEstimate: r.rough_estimate,
      weights: Object.entries(r.weights)
        .map(([id, w]) => ({ id: Number(id), name: nameOf(id), weight: w }))
        .sort((a, b) => b.weight - a.weight),
    },
    metrics: Object.fromEntries(METRIC_KEYS.map((m, i) => [m, summaryVM(m, rows[i])])),
    daily: Object.fromEntries(
      METRIC_KEYS.map((m, i) => [m, rows[i].daily.map((d) => ({ date: d.date, value: d[MEASURE_PARAM[m]] }))]),
    ),
  };
}

// Aligns seasons on the backend's `day` index; average and min–max use only the prior seasons returned.
export async function fetchSeasons(stationId, metric, signal) {
  const r = await get(`/api/stations/${stationId}/seasons?measure=${MEASURE_PARAM[metric]}`, signal);
  const current = r.seasons.find((s) => s.current);
  const prior = r.seasons.filter((s) => !s.current).slice(0, 4);
  const byDay = (s) => new Map(s.points.map((p) => [p.day, p]));
  const cur = byDay(current);
  const priorMaps = prior.map(byDay);
  const length = Math.max(...r.seasons.map((s) => s.points[s.points.length - 1].day)) + 1;
  const lastObservedDay = Math.max(...current.points.filter((p) => p.kind === "observed").map((p) => p.day));
  const start = current.points[0].date;
  const enough = (vals) => vals.length >= Math.min(2, priorMaps.length);
  const points = Array.from({ length }, (_, d) => {
    const c = cur.get(d);
    const vals = priorMaps.map((m) => m.get(d)?.total).filter((v) => v != null);
    return {
      day: d,
      label: shortDate(c?.date || addDaysIso(start, d)),
      date: c?.date || null,
      currentObserved: c && c.kind === "observed" ? c.total : null,
      currentForecast: c && (c.kind === "forecast" || d === lastObservedDay) ? c.total : null,
      avg: enough(vals) ? vals.reduce((a, b) => a + b, 0) / vals.length : null,
      range: enough(vals) ? [Math.min(...vals), Math.max(...vals)] : null,
      ...Object.fromEntries(priorMaps.map((m, k) => [`s${k}`, m.get(d)?.total ?? null])),
    };
  });
  return {
    metric,
    units: r.units,
    issueDate: r.issue_date,
    issueLabel: shortDate(r.issue_date),
    currentLabel: current.season,
    priorLabels: prior.map((s) => s.season),
    points,
    note: `Current season ${current.season} (observed, then the 7-day forecast) against ${prior.length} past seasons from the API. The average and min–max band are computed from those past seasons only.`,
  };
}

export const fetchForecastRows = (stationId, signal) =>
  get(`/api/forecast?station=${stationId}`, signal).then((rows) =>
    rows.map((r) => ({
      targetDate: r.target_date,
      issueDate: r.issue_date,
      horizon: r.horizon,
      chillHours: r.chill_hours,
      chillPortions: r.chill_portions,
      gdd: r.gdd,
      chillHoursStd: r.chill_hours_season_to_date,
      chillPortionsStd: r.chill_portions_season_to_date,
      gddStd: r.gdd_season_to_date,
    })),
  );

export const fetchMapGrid = (metric, value, signal) =>
  get(`/api/map?measure=${MEASURE_PARAM[metric]}&value=${value}`, signal).then((r) => {
    const vals = r.points.map((p) => p.value).filter((v) => v != null);
    return {
      metric,
      value,
      units: r.units,
      issueDate: r.issue_date,
      stations: r.stations,
      points: r.points,
      min: Math.min(...vals),
      max: Math.max(...vals),
    };
  });
